import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../../types';
import { getChatAvatarParticipants } from '../../utils';
import { useConversationActiveStatus } from '../../hooks/use-conversation-active-status';
import { GroupChatAvatar } from '../avatar/GroupChatAvatar';
import { ThumbnailAvatar } from '../avatar/ThumbnailAvatar';

/**
 * The avatar component for a conversation. This is determined by the conversation
 * type, participants, and thumbnail settings.
 */
export function ConversationAvatar({
    className,
    avatarClassName,
    fallbackClassName,
    conversation,
    professionalID,
    showActiveStatus = false,
}: {
    className?: string;
    avatarClassName?: string;
    fallbackClassName?: string;
    conversation: Conversation;
    /**
     * The ID of the professional if in a company inbox context.
     */
    professionalID: number | null;
    /**
     * Show the active status of the other participant of the 1-on-1 chat, or the
     * active status of the group chat. Defaults to `false`.
     */
    showActiveStatus?: boolean;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const isDirectConversation =
        conversation.__typename === 'DirectConversation';
    const isGroupChat = isDirectConversation && conversation.IsGroupChat;
    const participants = useMemo(() => {
        return getChatAvatarParticipants(
            conversation,
            isLoggedIn ? user.ID : 0,
            professionalID,
        );
    }, [conversation, isLoggedIn, user.ID, professionalID]);
    const activeStatus = useConversationActiveStatus(
        showActiveStatus ? conversation : null,
        professionalID,
    );

    return isDirectConversation && conversation.Thumbnail?.Link ? (
        <ThumbnailAvatar
            className={cn('size-12', className)}
            src={conversation.Thumbnail.Link}
            activeStatus={activeStatus ?? undefined}
        />
    ) : (
        <GroupChatAvatar
            className={cn('size-12', className)}
            avatarClassName={cn('size-12', avatarClassName)}
            fallbackClassName={cn('!text-6', fallbackClassName)}
            participants={participants}
            activeStatus={activeStatus ?? undefined}
            maxAvatars={isGroupChat ? 7 : 1}
        />
    );
}
